import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { isSubdomain, getMainDomainUrl } from "@/lib/subdomain";
import {
  Briefcase,
  Box,
  Code,
  Palette,
  Globe,
  Smartphone,
  Zap,
  Award,
  ArrowRight,
  Bot,
  Brain,
  Cloud,
  Gauge,
  Database,
  MessageSquare,
  Workflow,
  Languages,
  Layers,
  Plug,
  Sparkles,
  Shield,
  TrendingUp,
  Bug,
} from "lucide-react";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { navPopupCopy } from "@/lib/navPopupCopy";

interface PopupSection {
  title: string;
  categoryHref: string;
  items: {
    icon: ReactNode;
    title: string;
    description: string;
    href: string;
  }[];
}

interface NavigationPopupProps {
  isVisible: boolean;
  activeItem: string | null;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  closePopup?: () => void;
}

const popupStructure: Record<
  string,
  {
    categoryHref: string;
    items: { icon: ReactNode; href: string }[];
  }[]
> = {
  services: [
    {
      categoryHref: "/services/web-app",
      items: [
        {
          icon: <Globe className="w-5 h-5 text-foreground" />,
          href: "/services/web-app#web-development",
        },
        {
          icon: <Layers className="w-5 h-5 text-foreground" />,
          href: "/services/web-app#full-stack-web-apps",
        },
        {
          icon: <Palette className="w-5 h-5 text-foreground" />,
          href: "/services/web-app#website-customization",
        },
        {
          icon: <Smartphone className="w-5 h-5 text-foreground" />,
          href: "/services/web-app#android-app-development",
        },
        {
          icon: <Plug className="w-5 h-5 text-foreground" />,
          href: "/services/web-app#apis-integrations",
        },
      ],
    },
    {
      categoryHref: "/services/ai-automation",
      items: [
        {
          icon: <Brain className="w-5 h-5 text-foreground" />,
          href: "/services/ai-automation#ai-ml-solutions",
        },
        {
          icon: <Sparkles className="w-5 h-5 text-foreground" />,
          href: "/services/ai-automation#ai-technology-consulting",
        },
        {
          icon: <Bot className="w-5 h-5 text-foreground" />,
          href: "/services/ai-automation#ai-agents",
        },
        {
          icon: <MessageSquare className="w-5 h-5 text-foreground" />,
          href: "/services/ai-automation#ai-chatbot",
        },
        {
          icon: <Workflow className="w-5 h-5 text-foreground" />,
          href: "/services/ai-automation#automations-agents",
        },
        {
          icon: <Languages className="w-5 h-5 text-foreground" />,
          href: "/services/ai-automation#nlp",
        },
      ],
    },
    {
      categoryHref: "/services/devops-cloud",
      items: [
        {
          icon: <Cloud className="w-5 h-5 text-foreground" />,
          href: "/services/devops-cloud#devops-cloud-services",
        },
        {
          icon: <Gauge className="w-5 h-5 text-foreground" />,
          href: "/services/devops-cloud#speed-optimization",
        },
      ],
    },
    {
      categoryHref: "/services/support-maintenance",
      items: [
        {
          icon: <Shield className="w-5 h-5 text-foreground" />,
          href: "/services/support-maintenance#maintenance-support",
        },
        {
          icon: <Bug className="w-5 h-5 text-foreground" />,
          href: "/services/support-maintenance#bug-fixes",
        },
        {
          icon: <Palette className="w-5 h-5 text-foreground" />,
          href: "/services/support-maintenance#website-customization-support",
        },
        {
          icon: <TrendingUp className="w-5 h-5 text-foreground" />,
          href: "/services/support-maintenance#performance-optimization",
        },
      ],
    },
  ],
  products: [
    {
      categoryHref: "/products",
      items: [
        {
          icon: <Box className="w-5 h-5 text-foreground" />,
          href: "/products#saas",
        },
        {
          icon: <Zap className="w-5 h-5 text-foreground" />,
          href: "/products#themes",
        },
        {
          icon: <Code className="w-5 h-5 text-foreground" />,
          href: "/products#libraries",
        },
      ],
    },
    {
      categoryHref: "/products",
      items: [
        {
          icon: <Award className="w-5 h-5 text-foreground" />,
          href: "/products#templates",
        },
        {
          icon: <Globe className="w-5 h-5 text-foreground" />,
          href: "/products#tools",
        },
      ],
    },
  ],
};

const heroCtaHrefs: Record<string, string> = {
  services: "/services",
  products: "/products",
};

const sidebarStructure: Record<
  string,
  { icon: ReactNode; href: string }[]
> = {
  services: [
    { icon: <Code className="w-5 h-5" />, href: "/services/web-app" },
    {
      icon: <Database className="w-5 h-5" />,
      href: "/services/ai-automation",
    },
    {
      icon: <Briefcase className="w-5 h-5" />,
      href: "/services/devops-cloud",
    },
  ],
  products: [
    { icon: <Box className="w-5 h-5" />, href: "/products" },
    { icon: <Code className="w-5 h-5" />, href: "/products#opensource" },
    { icon: <Award className="w-5 h-5" />, href: "/products#premium" },
  ],
};

export function NavigationPopup({
  isVisible,
  activeItem,
  onMouseEnter,
  onMouseLeave,
  closePopup,
}: NavigationPopupProps) {
  const navigate = useNavigate();
  const lang = useSiteLanguage();
  const copy = navPopupCopy[lang];

  if (!activeItem || !popupStructure[activeItem]) {
    return null;
  }

  const key = activeItem as "services" | "products";
  const structure = popupStructure[key];
  const sectionCopy = copy[key];
  const heroCopy = copy.hero[key];
  const sidebarTitles = copy.sidebar[key];
  const sidebarBase = sidebarStructure[key];

  const sections: PopupSection[] = structure.map((section, sectionIndex) => ({
    title: sectionCopy[sectionIndex].title,
    categoryHref: section.categoryHref,
    items: section.items.map((item, itemIndex) => ({
      ...item,
      title: sectionCopy[sectionIndex].items[itemIndex].title,
      description: sectionCopy[sectionIndex].items[itemIndex].description,
    })),
  }));

  const hero = {
    ...heroCopy,
    ctaHref: heroCtaHrefs[key],
  };

  const sidebar = sidebarBase.map((link, index) => ({
    ...link,
    title: sidebarTitles[index],
  }));

  const handleNavigation = (href: string) => {
    if (typeof closePopup === "function") closePopup();

    if (isSubdomain("blog")) {
      window.location.href = getMainDomainUrl(href);
    } else {
      navigate(href);
    }
  };

  return (
    <div
      className={`fixed top-14 left-0 z-50 w-full rounded-b-2xl bg-background border-b border-border shadow-2xl overflow-hidden transition-all duration-300 ease-out transform ${
        isVisible
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 -translate-y-3 pointer-events-none"
      }`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="px-6 lg:px-12">
        <div className="py-8">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-4 pr-6 border-r border-border">
              <div className="mb-6">
                <h3 className="text-lg font-bold text-foreground mb-2">
                  {hero.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {hero.description}
                </p>
              </div>

              <div className="space-y-1">
                {sidebar.map((link, index) => (
                  <button
                    key={index}
                    onClick={() => handleNavigation(link.href)}
                    className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="text-muted-foreground group-hover:text-foreground transition-colors">
                        {link.icon}
                      </div>
                      <span className="text-sm font-medium text-foreground">
                        {link.title}
                      </span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                  </button>
                ))}
              </div>

              <button
                onClick={() => handleNavigation(hero.ctaHref)}
                className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg font-medium hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors"
              >
                {hero.cta}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            <div className="col-span-12 lg:col-span-8">
              <div
                className="grid gap-8"
                style={{
                  gridTemplateColumns: `repeat(${Math.min(sections.length, 4)}, 1fr)`,
                }}
              >
                {sections.map((section, sectionIndex) => (
                  <div key={sectionIndex} className="space-y-3">
                    <button
                      onClick={() => handleNavigation(section.categoryHref)}
                      className="group flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors"
                    >
                      {section.title}
                      <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                    </button>

                    <div className="space-y-1">
                      {section.items.map((item, itemIndex) => (
                        <button
                          key={itemIndex}
                          onClick={() => handleNavigation(item.href)}
                          className="w-full text-left p-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group"
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-0.5 text-muted-foreground group-hover:text-foreground transition-colors">
                              {item.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <h4 className="text-sm font-medium text-foreground group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">
                                  {item.title}
                                </h4>
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                {item.description}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
